import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import { FormControl } from '@angular/forms'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import { BehaviorSubject, forkJoin, merge, Observable, of, throwError } from 'rxjs'
import { catchError, concatMap, finalize, map, tap, withLatestFrom } from 'rxjs/operators'
import * as lodash from 'lodash'

import * as fromRoot from '@tv3/store/state'
import { createLocaleCompareSort, isEmptyTable } from '@tokeet-frontend/utility'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { TableService } from '@tv3/services/utils/table.service'
import {
  ConfirmDialogService,
  ConfirmDialogStatus,
  GroupRateService,
  LoadGroupRatesComplete,
  Rate,
  RateMap,
  RateService,
  RateType,
  Rental,
  RentalService,
  selectAllRentals,
  Toaster,
  toMoment,
  untilDestroy,
  updateRentalComplete,
} from '@tokeet/tv3-platform'
import { SelectableRow } from '@tokeet-frontend/utility'
import { UserStorage } from '@tv3/services/utils/storage.service'
import { DateRangePickerSelection } from '@tv3/shared/date-range-picker/date-range-picker.component'
import { Currency } from '@tv3/models/account/currency'
import { EditRateDialogService } from '../edit-rate-dialog/edit-rate-dialog.service'
import { EditRateDialogInput } from '../edit-rate-dialog/edit-rate-dialog.component'
import { AccountRatesDialogService } from '../account-rates-dialog/account-rates-dialog.service'
import { AccountRatesDialogTab } from '../account-rates-dialog/account-rates-dialog.component'
import * as R from 'ramda'

@Component({
  selector: 'app-rates-table',
  templateUrl: './rates-table.component.html',
  styleUrls: ['./rates-table.component.scss'],
})
export class RatesTableComponent extends SelectableRow<Rate> implements OnInit, OnChanges {
  @Input() rental: Rental

  @Output() changeRates = new EventEmitter()

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  isDeleting = false
  groupRatesLoaded = false

  tableType: TableType

  searchControl = new FormControl()
  categoryControl = new FormControl()
  periodControl = new FormControl()
  rentalsControl = new FormControl()

  rentalRates$ = new BehaviorSubject<Rate[]>([])
  groupRates$ = new BehaviorSubject<Rate[]>([])

  rates$ = new BehaviorSubject<Rate[]>([])

  filteredRentals$ = new BehaviorSubject<Rental[]>([])
  categories$: Observable<string[]>

  rentals: Rental[]

  currency: string | Currency

  displayedColumns = ['select', 'name', 'category', 'period', 'nightly', 'weekly', 'monthly', 'minimumStay', 'edit']

  dataSource = new MatTableDataSource<Rate>()

  isEmptyTable$ = isEmptyTable(this.dataSource)

  isSelectAllRenderedItems = true

  constructor(
    protected tableService: TableService,
    protected storage: UserStorage,
    private store: Store<fromRoot.State>,
    private actions$: Actions,
    private toaster: Toaster,
    private rentalService: RentalService,
    private rateService: RateService,
    private groupRateService: GroupRateService,
    private confirmDialog: ConfirmDialogService,
    private editRateDialog: EditRateDialogService,
    private accountRatesDialog: AccountRatesDialogService
  ) {
    super(tableService, storage)
  }

  ngOnInit() {
    this.tableType = this.isGroupRates ? TableType.GroupRates : TableType.RentalRates
    this.currency = this.isGroupRates ? null : this.rental.currency

    this.loadSavedTableState()
    this.loadFilters()

    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = createLocaleCompareSort(this.sortDataAccessor)
    this.setDataSource(this.dataSource)

    this.categories$ = this.rates$.pipe(
      map((rates) => this.rentalService.getUniqueCategories(rates)),
      tap((items) => {
        const chosenItem = this.categoryControl.value

        if (R.isNil(R.find((i) => i === chosenItem, items))) {
          this.categoryControl.patchValue(null)
        }
      })
    )

    if (this.isGroupRates) {
      this.prepareRentals()
      this.displayedColumns.splice(4, 0, 'rentals')
    }

    this.prepareRates()

    merge(
      this.rates$,
      this.searchControl.valueChanges,
      this.categoryControl.valueChanges,
      this.periodControl.valueChanges,
      this.rentalsControl.valueChanges
    )
      .pipe(
        tap(() => {
          this.saveFilters()
          this.filterRates()
        }),
        untilDestroy(this)
      )
      .subscribe()

    this.rates$.next(this.rates$.value)
  }

  ngOnChanges(changes: SimpleChanges) {
    this.rental = lodash.get(changes, ['rental', 'currentValue'], this.rental)

    if (!this.isGroupRates) this.prepareRentalRates()
  }

  isRowSelectable = (rate: Rate) => {
    return this.isGroupRates || !this.isGroupRate(rate)
  }

  onMasterToggle(checked: boolean) {
    if (checked) {
      this.isSelectAllRenderedItems = true
      this.masterToggle(true)
    } else {
      this.unselectAll()
    }
  }

  onSelectAll(all = false) {
    this.unselectAll()
    if (all) {
      this.masterToggle(false)
      this.isSelectAllRenderedItems = false
    } else {
      this.masterToggle(true)
      this.isSelectAllRenderedItems = true
    }
  }

  searchRate(rate: Rate) {
    this.searchControl.setValue(rate.name)
  }

  hasRentalRates = (rates: Rate[]) => {
    return !!rates.find((rate) => !this.isGroupRate(rate))
  }

  get isGroupRates() {
    return !this.rental
  }

  get rateLabel() {
    return this.isGroupRates ? 'Group Rate' : 'Rate'
  }

  get rateIcon() {
    return this.isGroupRates ? 'fas fa-hands-usd' : 'far fa-calculator'
  }

  isGroupRate(rate: Rate) {
    return !rate.rentalId && rate.rentalIds
  }

  sortDataAccessor = (rate: Rate, property: string) => {
    switch (property) {
      case 'period':
        return rate.start
      case 'category':
        return this.getRateCategory(rate)
      case 'rentals':
        return rate.rentalIds ? rate.rentalIds.length : 0
      default:
        return rate[property]
    }
  }

  clearFilters() {
    this.storage.remove(this.filtersStorageKey)
    this.loadFilters()
  }

  filterRates() {
    const search = lodash.trim(this.searchControl.value).toLowerCase()
    const category = lodash.toString(this.categoryControl.value)
    const period = this.periodControl.value as DateRangePickerSelection

    let rates = this.rates$.value

    if (search) {
      const dateFormat = 'MMM D, YYYY'

      rates = rates.filter((rate) => {
        if (lodash.toString(rate.name).toLowerCase().includes(search)) return true
        if (lodash.toString(rate.category).toLowerCase().includes(search)) return true

        const textCategories = rate.categories && lodash.toString(rate.categories.join('\n'))
        if (textCategories && textCategories.toLowerCase().includes(search)) return true

        const textPeriod = `${toMoment(rate.start).format(dateFormat)} - ${toMoment(rate.end).format(dateFormat)}`
        if (textPeriod && textPeriod.toLowerCase().includes(search)) return true

        return false
      })
    }

    if (category && category !== '*') {
      rates = rates.filter((rate) => {
        return lodash.toString(rate.category) === category || (rate.categories && rate.categories.includes(category))
      })
    }

    if (period) {
      rates = rates.filter((rate) => {
        const start = toMoment(rate.start).startOf('date').unix()
        const end = toMoment(rate.end).startOf('date').unix()

        return (
          (start >= period.from && start <= period.to) ||
          (end >= period.from && end <= period.to) ||
          (start < period.from && end > period.to)
        )
      })
    }

    if (this.isGroupRates) {
      const rentalIds = lodash.keyBy(this.rentalsControl.value as string[])

      if (!lodash.isEmpty(rentalIds)) {
        rates = rates.filter((rate) => {
          return lodash.find(rate.rentalIds, (rentalId) => rentalIds[rentalId])
        })
      }
    }

    this.dataSource.data = rates
  }

  getRateCategory(rate: Rate) {
    if (rate.type === 'promotion') {
      return (rate.categories || []).join(', ') || 'promotion'
    } else {
      return rate.category || ''
    }
  }

  getRowTooltip = (rate: Rate) => {
    if (this.isGroupRates || !this.isGroupRate(rate)) return null
    return 'This is a group rate and may only be managed in the group rates table.'
  }

  getRentalsTooltip = (rentalIds: string) => {
    const rentals = lodash.keyBy(this.rentals, (rental) => rental.id)

    const rentalNames = lodash.map(rentalIds, (rentalId) => {
      const rental = rentals[rentalId]

      return rental && rental.name
    })

    return lodash.compact(rentalNames).join(', ')
  }

  onAddStandardRate() {
    this.openEditRateDialog(null, 'standard')
  }

  onAddDynamicRate() {
    this.openEditRateDialog(null, 'dynamic')
  }

  onEdit(rate: Rate) {
    if (!this.isGroupRates && this.isGroupRate(rate)) {
      this.accountRatesDialog.openSide({ activeTab: AccountRatesDialogTab.GroupRates, focusGroupRate: rate })
      return
    }

    this.openEditRateDialog(rate)
  }

  onDelete(rate?: Rate) {
    const rates: Rate[] = rate ? [rate] : this.getSelected()

    if (this.isGroupRates) {
      this.onDeleteGroupRates(rates)
    } else {
      this.onDeleteRentalRates(rates)
    }
  }

  private prepareRates() {
    this.prepareGroupRates()

    if (this.isGroupRates) {
      this.groupRates$
        .pipe(
          tap((rates) => {
            this.rates$.next(rates)
          }),
          untilDestroy(this)
        )
        .subscribe()

      return
    }

    merge(this.rentalRates$, this.groupRates$)
      .pipe(
        tap(() => {
          const rentalRates = [...this.rentalRates$.value]
          const groupRates = this.groupRates$.value

          groupRates.forEach((rate) => {
            if (lodash.includes(rate.rentalIds, this.rental.id)) {
              rentalRates.push(rate)
            }
          })

          this.rates$.next(rentalRates)
        })
      )
      .subscribe()
  }

  private prepareGroupRates() {
    this.groupRateService
      .all()
      .pipe(
        tap((rates) => {
          this.groupRates$.next(rates)
          this.groupRatesLoaded = true
        })
      )
      .subscribe()

    this.actions$
      .pipe(
        ofType(LoadGroupRatesComplete.type),
        tap(({ rates }: { rates: Rate[] }) => {
          this.groupRates$.next(rates)
        }),
        untilDestroy(this)
      )
      .subscribe()
  }

  private prepareRentalRates() {
    const rates = [...(this.rental.rates || []), ...(this.rental.promotions || [])]

    this.rentalRates$.next(rates)
  }

  private prepareRentals() {
    this.rates$
      .pipe(
        withLatestFrom(this.store.select(selectAllRentals)),
        map(([rates, rentals]) => {
          this.rentals = rentals

          const rentalIds = lodash
            .chain(rates)
            .map((rate) => rate.rentalIds)
            .flatten()
            .keyBy()
            .value()

          rentals = rentals.filter((rental) => rentalIds[rental.id])

          this.filteredRentals$.next(rentals)
        }),
        untilDestroy(this)
      )
      .subscribe()
  }

  private onDeleteGroupRates(rates: Rate[]) {
    const textRates = rates.length > 1 ? 'these group rates' : 'this group rate'

    let text = `Are you sure you want to delete ${textRates}?`

    const rentalsById = lodash.keyBy(this.rentals, (rental) => rental.id)

    let cleanupRateMappings: RateMap[] = []
    let cleanupRentals: Rental[] = []

    const groupRatesCountByCategory = lodash.countBy(this.rates$.value, (r) => r.category)
    const deletingGroupRatesCountByCategory = lodash.countBy(rates, (r) => r.category)

    rates.forEach((groupRate) => {
      if (groupRatesCountByCategory[groupRate.category] !== deletingGroupRatesCountByCategory[groupRate.category])
        return

      groupRate.rentalIds.forEach((rentalId) => {
        const rental = rentalsById[rentalId]
        if (!rental || lodash.isEmpty(rental.rateMap)) return

        const count = lodash.sumBy(rental.rates, (r) => (r.category === groupRate.category ? 1 : 0))
        if (count > 0) return

        let hasRateMappings = false

        rental.rateMap.forEach((rateMap) => {
          if (rateMap.category === groupRate.category) {
            cleanupRateMappings.push(rateMap)
            hasRateMappings = true
          }
        })

        if (hasRateMappings) cleanupRentals.push(rental)
      })
    })

    cleanupRateMappings = lodash.uniqBy(cleanupRateMappings, (rateMap) => rateMap.key)
    cleanupRentals = lodash.uniqBy(cleanupRentals, (rental) => rental.id)

    const countRateMappings = cleanupRateMappings.length
    const countRentals = cleanupRentals.length

    if (countRateMappings) {
      // tslint:disable-next-line: max-line-length
      text = `This action will also delete ${countRateMappings} rate ${
        countRateMappings > 1 ? 'mappings' : 'mapping'
      } of ${countRentals} ${countRentals > 1 ? 'rentals' : 'rental'}. ${text}`
    }

    this.confirmDialog
      .confirm({
        title: `Delete ${textRates}?`,
        body: text,
      })
      .subscribe(() => {
        this.deleteGroupRates(rates, cleanupRentals)
      })
  }

  private onDeleteRentalRates(rates: Rate[]) {
    const textRates = rates.length > 1 ? 'these rates' : 'this rate'

    let text = `Are you sure you want to delete ${textRates}?`

    let countRateMappings = 0

    const groupRates = this.rates$.value.filter((rate) => lodash.includes(rate.rentalIds, this.rental.id))

    const ratesCountByCategory = lodash.countBy(groupRates.concat(this.rental.rates || []), (r) => r.category)
    const deletingRatesCountByCategory = lodash.countBy(rates, (r) => r.category)

    lodash.forEach(this.rental.rateMap, (rateMapping) => {
      if (ratesCountByCategory[rateMapping.category] === deletingRatesCountByCategory[rateMapping.category])
        countRateMappings += 1
    })

    if (countRateMappings) {
      text = `This action will also delete ${countRateMappings} rate ${
        countRateMappings > 1 ? 'mappings' : 'mapping'
      }. ${text}`
    }

    this.confirmDialog
      .confirm({
        title: `Delete ${textRates}?`,
        body: text,
      })
      .subscribe(() => {
        this.deleteRentalRates(rates, countRateMappings > 0)
      })
  }

  private deleteGroupRates(rates: Rate[], cleanupRentals: Rental[]) {
    this.isDeleting = true

    this.groupRateService
      .deleteRates(rates)
      .pipe(
        concatMap((updatedRates) => {
          if (!cleanupRentals.length) return of({ updatedRates, updatedRateMappings: [] as RateMap[][] })

          return forkJoin(
            cleanupRentals.map((rental) => {
              return this.rentalService.cleanRateMappings(rental, this.rentalService.getUniqueCategories(rental.rates))
            })
          ).pipe(map((updatedRateMappings) => ({ updatedRates, updatedRateMappings })))
        }),
        tap(({ updatedRates, updatedRateMappings }) => {
          this.toaster.success(`${rates.length > 1 ? 'Group rates' : 'Group rate'} deleted successfully.`)
          this.updateGroupRates(updatedRates)

          cleanupRentals.forEach((rental, index) => {
            this.store.dispatch(
              updateRentalComplete({
                update: {
                  id: rental.id,
                  changes: { rateMap: updatedRateMappings[index] },
                },
              })
            )
          })
        }),
        catchError((error) => {
          this.toaster.error(`Unable to delete ${rates.length > 1 ? 'rates' : 'rate'}.`)
          return throwError(error)
        }),
        finalize(() => {
          this.isDeleting = false
        })
      )
      .subscribe()
  }

  private updateGroupRates(rates: Rate[]) {
    this.store.dispatch(LoadGroupRatesComplete({ rates }))
  }

  private deleteRentalRates(rates: Rate[], shouldCleanRateMappings: boolean) {
    this.isDeleting = true

    this.rateService
      .deleteRates(this.rental.id, rates)
      .pipe(
        concatMap((updatedRates) => {
          if (!shouldCleanRateMappings) return of({ updatedRates, updatedRateMappings: null as RateMap[] })
          return this.rentalService
            .cleanRateMappings(this.rental, this.rentalService.getUniqueCategories(updatedRates.rates))
            .pipe(map((updatedRateMappings) => ({ updatedRates, updatedRateMappings })))
        }),
        tap(({ updatedRates, updatedRateMappings }) => {
          this.toaster.success(`${rates.length > 1 ? 'Rates' : 'Rate'} deleted successfully.`)
          this.updateRentalRates(updatedRates.rates, updatedRates.promotions, updatedRateMappings)
        }),
        catchError((error) => {
          this.toaster.error(`Unable to delete ${rates.length > 1 ? 'rates' : 'rate'}.`)
          return throwError(error)
        }),
        finalize(() => {
          this.isDeleting = false
        })
      )
      .subscribe()
  }

  private updateRentalRates(rates: Rate[], promotions: Rate[], rateMappings: RateMap[]) {
    const changes: Partial<Rental> = {}

    if (rates) {
      changes.rates = rates
    }

    if (promotions) {
      changes.promotions = promotions
    }

    if (rateMappings) {
      changes.rateMap = rateMappings
    }

    this.store.dispatch(
      updateRentalComplete({
        update: {
          id: this.rental.id,
          changes,
        },
      })
    )

    this.changeRates.emit()
  }

  private openEditRateDialog(rate: Rate = null, type: RateType = null) {
    let isGroupRate = false

    if ((!rate && this.isGroupRates) || (rate && this.isGroupRate(rate))) {
      isGroupRate = true
    }

    const params: EditRateDialogInput = {
      isGroupRate,
      rate,
      type,
    }

    if (isGroupRate) {
      params.groupRates = this.groupRates$.value
      params.rentals = this.rentals
    } else {
      params.rental = this.rental
    }

    this.editRateDialog
      .open(params)
      .afterClosed()
      .subscribe((result) => {
        if (result && result.groupRates) {
          this.groupRates$.next(result.groupRates)
        }
      })
  }

  private saveFilters() {
    const search = this.searchControl.value
    const category = this.categoryControl.value
    const period = this.periodControl.value
    const rentals = this.rentalsControl.value

    this.storage.set(this.filtersStorageKey, {
      search,
      category,
      period,
      rentals,
    })
  }

  private loadFilters() {
    const data = this.storage.get(this.filtersStorageKey) || {}

    this.searchControl.setValue(data.search || '')
    this.categoryControl.setValue(data.category || '')
    this.periodControl.setValue(data.period || null)
    this.rentalsControl.setValue(data.rentals || [])
  }

  private get filtersStorageKey() {
    return this.isGroupRates ? 'GROUP_RATES_TABLE_FILTERS' : 'RENTAL_RATES_TABLE_FILTERS'
  }
}
